import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Clock, Mail, MapPin, Phone } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { Kicker, Reveal } from "@/components/Reveal";
import PageHeader from "@/components/PageHeader";
import Magnetic from "@/components/motion/Magnetic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "An educator",
  "An academic leader",
  "An industry professional",
  "A college, institution or organisation",
  "Interested in a founding board seat",
];

const inputCls =
  "peer w-full border-0 border-b border-line bg-transparent py-3 text-base text-ink placeholder:text-ink/35 focus:border-pine focus:ring-0 transition-colors duration-300";

function Field({ label, children }) {
  return (
    <div className="group/field">
      <label className="font-display text-xs font-semibold uppercase tracking-[0.16em] text-muted transition-colors group-focus-within/field:text-pine">
        {label}
      </label>
      {children}
    </div>
  );
}

export default function Contact() {
  const c = useContent();
  const [form, setForm] = useState({ name: "", email: "", role: ROLES[0], organisation: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const rows = [
    {
      icon: Mail,
      label: "Email",
      value: c.get("contact_info", "email"),
      href: `mailto:${c.get("contact_info", "email")}`,
      id: "email",
    },
    {
      icon: Phone,
      label: "Phone",
      value: c.get("contact_info", "phone"),
      href: `tel:${c.get("contact_info", "phone").replace(/\s/g, "")}`,
      id: "phone",
    },
    { icon: MapPin, label: "Office", value: c.get("contact_info", "office"), id: "office" },
    { icon: Clock, label: "Response", value: "Within 5 working days", id: "web" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHeader
        testid="contact-header"
        kicker="Contact"
        title="Tell us about yourself."
        subtitle="Educators, academic leaders, colleges and partners: start a conversation and we will reply within 5 working days."
      />

      <section className="border-b border-line">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-16 px-5 py-16 md:grid-cols-12 md:py-24">
          <div className="md:col-span-4">
            <Kicker>Reach us</Kicker>
            <div className="mt-8">
              {rows.map((r, i) => (
                <Reveal
                  key={r.id}
                  delay={i * 0.08}
                  className="group flex gap-5 border-t border-line py-6 last:border-b"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-bone text-pine transition-colors duration-500 group-hover:bg-pine group-hover:text-white">
                    <r.icon className="h-4 w-4" strokeWidth={1.6} />
                  </span>
                  <div>
                    <p className="font-display text-xs font-semibold uppercase tracking-[0.16em] text-goldtext">
                      {r.label}
                    </p>
                    {r.href ? (
                      <a
                        href={r.href}
                        className="link-line mt-1 inline-block font-display text-lg font-medium text-pine"
                        data-testid={`contact-${r.id}`}
                      >
                        {r.value}
                      </a>
                    ) : (
                      <p className="mt-1 max-w-xs leading-relaxed text-body" data-testid={`contact-${r.id}`}>
                        {r.value}
                      </p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.4} className="mt-10 hidden overflow-hidden md:block">
              <img
                src="/images/path_pune.jpg"
                alt="Pune"
                className="img-editorial aspect-[4/3] w-full rounded-[10px] object-cover"
                loading="lazy"
              />
              <p className="mt-3 text-xs text-ink/45">Pune, Maharashtra — where Veridian begins.</p>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="md:col-span-7 md:col-start-6">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative overflow-hidden rounded-[10px] bg-pine p-10 text-white md:p-16"
                data-testid="enquiry-success"
              >
                <img
                  src="/brand/leaf.png"
                  alt=""
                  aria-hidden
                  className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 object-contain opacity-20"
                />
                <CheckCircle2 className="h-10 w-10 text-terra" />
                <h2 className="mt-6 font-display text-3xl font-semibold !text-white md:text-4xl">
                  Thank you, {form.name.split(" ")[0]}.
                </h2>
                <p className="mt-4 max-w-md leading-relaxed text-[#CFE3D9]">
                  Your enquiry has been received. We reply within 5 working days at{" "}
                  <span className="font-semibold text-white">{form.email}</span>.
                </p>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                className="rounded-[10px] border border-line bg-surface p-8 shadow-[0_10px_30px_rgba(18,33,27,0.06)] md:p-12"
                data-testid="enquiry-form"
              >
                <p className="font-display text-2xl font-semibold text-ink">Send an enquiry</p>
                <div className="mt-10 space-y-10">
                  <div className="grid gap-10 md:grid-cols-2">
                    <Field label="Name *">
                      <input
                        required
                        value={form.name}
                        onChange={set("name")}
                        className={inputCls}
                        placeholder="Your full name"
                        data-testid="enquiry-name-input"
                      />
                    </Field>
                    <Field label="Email *">
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        className={inputCls}
                        placeholder="you@institution.edu"
                        data-testid="enquiry-email-input"
                      />
                    </Field>
                  </div>
                  <div className="grid gap-10 md:grid-cols-2">
                    <Field label="I am">
                      <select
                        value={form.role}
                        onChange={set("role")}
                        className={`${inputCls} cursor-pointer`}
                        data-testid="enquiry-role-select"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Institution / organisation">
                      <input
                        value={form.organisation}
                        onChange={set("organisation")}
                        className={inputCls}
                        placeholder="Where you teach or work"
                        data-testid="enquiry-org-input"
                      />
                    </Field>
                  </div>
                  <Field label="Message">
                    <textarea
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      className={`${inputCls} resize-none`}
                      placeholder="Tell us what you are looking for"
                      data-testid="enquiry-message-input"
                    />
                  </Field>
                  <Magnetic strength={0.2}>
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="enquiry-submit-button"
                      className="btn-sweep btn-sweep-moss group/btn inline-flex items-center gap-2 rounded-md bg-pine px-[1.35rem] py-[0.8rem] font-display text-[0.98rem] font-semibold text-white transition-transform duration-500 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                      <span className="relative z-10">{sending ? "Sending..." : "Send enquiry"}</span>
                      <ArrowRight className="relative z-10 h-4 w-4 transition-transform duration-500 group-hover/btn:translate-x-1" />
                    </button>
                  </Magnetic>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
